import { Link } from "react-router-dom";
import { ShieldAlert, Quote, FileCheck, ArrowRight, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { trackCTAClick } from "../utils/analytics";
import Seo from "../components/Seo";
import PublicHeader from "../components/PublicHeader";
import Footer from "../components/Footer";

const FEATURES = [
  {
    icon: ShieldAlert,
    title: "Risk-Scored Case Review",
    desc: "Risk-scored scenario analysis before you act — understand grievance exposure and procedural requirements before a decision becomes a dispute.",
  },
  {
    icon: Quote,
    title: "Clause-Cited Answers",
    desc: "Every answer cites the specific article and language from your agreement, so you can verify it against the source in seconds.",
  },
  {
    icon: FileCheck,
    title: "An Audit Trail You Can Stand Behind",
    desc: "Answer history and exportable documentation you can stand behind in a grievance meeting.",
  },
];

const DIFF_POINTS = [
  {
    title: "Cited clauses from YOUR agreement — not confident guessing",
    desc: "General AI tools answer from internet training data and guess confidently. CBAIQ is indexed to your agreement and cites the exact articles it relied on.",
  },
  {
    title: "Connects LOUs, past settlements, and supporting documents",
    desc: "Real interpretation questions rarely live in one document. CBAIQ reads your agreement together with its letters of understanding and settlement history.",
  },
  {
    title: "An exportable, defensible record",
    desc: "Every answer is logged and exportable — documentation you can bring to a grievance meeting, not a chat transcript that disappears.",
  },
];

export default function Landing() {
  const handleCTAClick = (name) => trackCTAClick(name);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Seo
        title="CBAIQ | AI-Powered Collective Agreement Interpretation"
        description="CBAIQ gives labour relations teams and front-line supervisors instant, clause-cited answers and grievance-risk scores from their own collective agreements. Decisions in minutes, not grievance meetings."
        path="/"
      />
      <PublicHeader />

      <div className="flex-1">
        {/* HERO */}
        <section className="pt-20 sm:pt-28 pb-16 px-4" data-testid="hero-section">
          <div className="max-w-3xl mx-auto">
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight tracking-tight text-foreground mb-6">
              Know what your collective agreement allows — before it becomes a grievance.
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-9">
              CBAIQ gives labour relations teams and front-line supervisors instant, clause-cited answers
              and grievance-risk scores from their own agreements. Decisions in minutes, not grievance meetings.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/pricing">
                <Button size="lg" className="w-full sm:w-auto" data-testid="hero-get-started-btn" onClick={() => handleCTAClick('hero_start_guided_pilot')}>
                  Start a guided pilot
                </Button>
              </Link>
              <Link to="/how-it-works">
                <Button size="lg" variant="outline" className="w-full sm:w-auto" data-testid="hero-how-it-works-btn" onClick={() => handleCTAClick('hero_see_how_it_works')}>
                  See how it works
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* PAIN */}
        <section className="py-16 border-y border-border/40 bg-muted/20 px-4" data-testid="pain-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-6 leading-tight">
              Every interpretation question is a grievance waiting to happen.
            </h2>
            <div className="space-y-4 text-sm text-muted-foreground leading-relaxed">
              <p>
                Supervisors make scheduling, overtime, and discipline decisions every day — and each one is
                governed by agreement language they don't have time to parse. When the interpretation is wrong,
                the cost compounds: hours of management time consumed per grievance, and{" "}
                <span className="text-foreground font-medium">$20,000–$75,000+ if it reaches arbitration</span>.
              </p>
              <p>
                The alternative isn't cheap either: routine interpretation questions routed to outside counsel
                run <span className="text-foreground font-medium">$400+/hour</span> — for answers that are
                already written in your own agreement.
              </p>
              <p>
                CBAIQ puts the agreement's actual language in front of the people making the decision, at the
                moment they're making it.
              </p>
            </div>
          </div>
        </section>

        {/* FEATURES — risk-scored case review leads */}
        <section className="py-16 px-4" data-testid="features-section">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-3">
              Built to Keep Decisions Out of Grievance Meetings
            </h2>
            <p className="text-sm text-muted-foreground max-w-lg mb-10">
              Purpose-built agreement intelligence — not a general-purpose chatbot.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {FEATURES.map(({ icon: Icon, title, desc }, i) => (
                <div key={title} className={`border rounded-lg p-7 ${i === 0 ? 'border-foreground/25 shadow-sm' : 'border-border'}`} data-testid={`feature-card-${i}`}>
                  <Icon className="w-5 h-5 text-muted-foreground mb-4" />
                  <h3 className="text-base font-semibold text-foreground mb-2">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* DIFFERENTIATION */}
        <section className="py-16 border-y border-border/40 bg-muted/20 px-4" data-testid="differentiation-section">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-8">
              Why not just use ChatGPT or Copilot?
            </h2>
            <div className="space-y-6">
              {DIFF_POINTS.map(({ title, desc }) => (
                <div key={title}>
                  <h3 className="text-sm font-semibold text-foreground mb-1.5">{title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>
            <Link to="/cbaiq-vs-chatgpt" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground mt-8 hover:gap-2.5 transition-all" data-testid="vs-chatgpt-link" onClick={() => handleCTAClick('home_vs_chatgpt')}>
              See the full comparison <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* TRUST */}
        <section className="py-16 px-4" data-testid="trust-section">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-5">
              <Lock className="w-6 h-6 text-muted-foreground mt-1 flex-shrink-0" />
              <div>
                <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-4">
                  Your Agreements Stay Yours.
                </h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                  Uploaded agreements are <span className="text-foreground font-medium">never used for model
                  training</span> and are <span className="text-foreground font-medium">never externally
                  indexed</span>. Documents remain under your organization's control, with institutional-grade
                  access handling and a full answer history.
                </p>
                <Link to="/security" className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:gap-2.5 transition-all" data-testid="security-link" onClick={() => handleCTAClick('home_security')}>
                  Read about security &amp; accuracy <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* WHO USES */}
        <section className="py-10 border-t border-border/40 px-4" data-testid="who-uses-section">
          <div className="max-w-3xl mx-auto">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Who uses CBAIQ:</span> HR &amp; labour relations
              leaders, front-line supervisors and managers at unionized employers — also used by union
              representatives, labor negotiators, and public sector locals.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="py-20 border-t border-border/40 bg-muted/20 px-4" data-testid="final-cta">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold text-foreground mb-4">
              Get answers from your agreement this week.
            </h2>
            <p className="text-sm text-muted-foreground mb-8 max-w-md mx-auto">
              Guided 14-day pilot — upload one agreement, get 20 answered questions, onboarding call included. No credit card.
            </p>
            <div className="flex justify-center">
              <Link to="/pricing">
                <Button size="lg" data-testid="cta-get-started-btn" onClick={() => handleCTAClick('cta_start_guided_pilot')}>
                  Start a guided pilot
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
}
